<template>
  <div class="menu-hiding">
    <div ref="root" class="menu-hiding-items">
      <slot />
    </div>

    <button
      v-if="isShowButton"
      ref="button"
      type="button"
      class="menu-hid-button"
      @click="handlerOpen"
    >
      {{ messages?.information }}
      <svg class="menu-hid-button__icon">
        <use xlink:href="#svg-arrow-down"></use>
      </svg>
    </button>
    <Teleport to="body">
      <div v-show="isOpen" ref="hiddenItems" class="menu-hid" :style="hiddenItemsStyles"></div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, reactive, onBeforeUnmount, computed } from 'vue';
import { useResultsStore } from '@/storePinia/stores/results';

const root = ref<HTMLDivElement | null>(null);
const hiddenItems = ref<HTMLDivElement | null>(null);
const button = ref<HTMLButtonElement | null>(null);
const isOpen = ref(false);
const isShowButton = ref(false);

const resultsStore = useResultsStore();

const messages = computed(() => resultsStore?.menus?.mainMenu?.messages);

const hiddenItemsStyles = reactive({
  top: '0px',
  left: '0px',
});

const setHiddenMenuStyles = () => {
  if (button.value) {
    const rect = button.value.getBoundingClientRect();

    hiddenItemsStyles.top = `${rect.top + 30}px`;
    hiddenItemsStyles.left = `${rect.left}px`;
  }
};

const handlerOpen = () => {
  setHiddenMenuStyles();
  isOpen.value = !isOpen.value;
};

const observerCallBack = (entries: IntersectionObserverEntry[]) => {
  entries.forEach((entry) => {
    if (entry.intersectionRatio < 1) {
      entry.target.classList.add('hidden');

      const copy = entry.target.cloneNode(true);
      copy.classList.remove('header-top-menu__link');
      if (hiddenItems.value && copy) {
        hiddenItems.value.prepend(copy);
      }
      isShowButton.value = true;
      return;
    }
    entry.target.classList.remove('hidden');

    const elementWillRemove = hiddenItems.value?.querySelector(
      `[data-id='${entry.target.dataset.id}']`
    );

    if (elementWillRemove) {
      elementWillRemove.remove();

      if (isShowButton.value && hiddenItems.value) {
        isShowButton.value = !!hiddenItems.value.childElementCount;
      }
    }
  });
};

const options: IntersectionObserverInit = {
  root: root.value,
  rootMargin: '0px 10px 0px 0px',
  threshold: 1,
};

const intersectionObserver: IntersectionObserver = new IntersectionObserver(
  observerCallBack,
  options
);

const handlerAnotherClick = (e) => {
  if (!e.target.closest('.menu-hid-button')) {
    isOpen.value = false;
  }
};
const handlerResize = () => {
  isOpen.value = false;
};

onMounted(() => {
  if ('IntersectionObserver' in window && root.value?.children) {
    Array.from(root.value?.children).forEach((entry, i) => {
      if (!entry.classList.contains('menu-hid-button')) {
        entry.dataset.id = i;
        intersectionObserver.observe(entry);
      }
    });

    document.addEventListener('click', handlerAnotherClick);
    window.addEventListener('resize', handlerResize);
    document.addEventListener('scroll', handlerResize);
  }
});

onBeforeUnmount(() => {
  if ('IntersectionObserver' in window && root.value?.children) {
    Array.from(root.value?.children).forEach((entry, i) => {
      if (!entry.classList.contains('menu-hid-button') && intersectionObserver) {
        intersectionObserver.unobserve(entry);
      }
    });

    document.removeEventListener('click', handlerAnotherClick);
    window.removeEventListener('resize', handlerResize);
    document.removeEventListener('scroll', handlerResize);
  }
});
</script>

<style lang="scss" scoped>
.menu-hiding {
  display: flex;
  align-items: center;
  text-wrap: nowrap;
  overflow: hidden;
  position: relative;
  padding-right: 22px;
  margin-right: 16px;
  :deep(.hidden) {
    visibility: hidden;
  }
}
.menu-hiding-items {
  overflow-x: hidden;
}
.menu-hid {
  position: absolute;
  font-size: 14px;
  font-weight: 500;
  line-height: 125%;
  background-color: #fff;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  z-index: 1;
  display: grid;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.1);
  & > :deep(*) {
    padding: 8px 16px;
  }
}
.menu-hid-button {
  display: flex;
  align-items: center;
  height: 18px;
  &__icon {
    width: 18px;
    height: 18px;
    fill: #fff;
    margin-left: 4px;
  }
}
</style>
